import React from "react";
import { Match } from "../types/tournament";
import { Trophy, Users, Minus } from "lucide-react";

interface CourtDisplayProps {
  match: Match;
  onEnterScore: () => void;
  liveScore?: { team1: number; team2: number };
  onLiveScoreUpdate?: (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => void;
}

const CourtDisplay: React.FC<CourtDisplayProps> = ({
  match,
  onEnterScore,
  liveScore,
  onLiveScoreUpdate,
}) => {
  const getCourtStatus = () => {
    if (match.isBye) return "bye";
    if (match.result?.completed) return "completed";
    return "active";
  };

  const status = getCourtStatus();
  const isCompleted = status === "completed";
  const isBye = status === "bye";

  const getStatusColor = () => {
    switch (status) {
      case "active":
        return "border-primary-500 bg-primary-50";
      case "completed":
        return "border-success-500 bg-success-50";
      case "bye":
        return "border-warning-500 bg-warning-50";
      default:
        return "border-gray-300 bg-gray-50";
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "active":
        return "In Progress";
      case "completed":
        return "Completed";
      case "bye":
        return "Bye";
      default:
        return "Empty";
    }
  };

  return (
    <div
      className={`court-card border-2 rounded-lg p-3 sm:p-4 ${getStatusColor()} transition-all duration-200 hover:shadow-md cursor-pointer`}
      onClick={onEnterScore}
    >
      {/* Court Header */}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base sm:text-lg font-bold text-gray-900">Court {match.court}</h3>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            status === "active"
              ? "bg-primary-100 text-primary-800"
              : status === "completed"
              ? "bg-success-100 text-success-800"
              : status === "bye"
              ? "bg-warning-100 text-warning-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {getStatusText()}
        </span>
      </div>

      {/* Match Content */}
      <div className="space-y-3">
        <div className="space-y-3">
          {isBye ? (
            <div className="text-center py-4">
              <Users className="w-8 h-8 text-warning-500 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">{match.team1.name}</p>
              <p className="text-sm text-gray-600">Bye Round</p>
            </div>
          ) : (
            <>
              {/* Team 1 */}
              <div className="flex justify-between items-center p-2 bg-white rounded border">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                    {match.team1.name}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {match.team1.player1} & {match.team1.player2}
                  </p>
                </div>
                {isCompleted && (
                  <div className="text-right ml-2">
                    <span
                      className={`text-base sm:text-lg font-bold ${
                        match.result?.winner === match.team1.id
                          ? "text-success-600"
                          : "text-gray-400"
                      }`}
                    >
                      {match.result?.team1Score}
                    </span>
                  </div>
                )}
              </div>

              {/* VS */}
              <div className="text-center">
                <span className="text-gray-500 font-medium">VS</span>
              </div>

              {/* Team 2 */}
              <div className="flex justify-between items-center p-2 bg-white rounded border">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                    {match.team2.name}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {match.team2.player1} & {match.team2.player2}
                  </p>
                </div>
                {isCompleted && (
                  <div className="text-right ml-2">
                    <span
                      className={`text-base sm:text-lg font-bold ${
                        match.result?.winner === match.team2.id
                          ? "text-success-600"
                          : "text-gray-400"
                      }`}
                    >
                      {match.result?.team2Score}
                    </span>
                  </div>
                )}
              </div>

              {/* Live Score Input */}
              {!isCompleted && onLiveScoreUpdate && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="text-sm font-medium text-blue-900 mb-2">
                    Live Score Entry
                  </div>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      min="0"
                      max="13"
                      value={liveScore?.team1 || 0}
                      onChange={(e) =>
                        onLiveScoreUpdate(
                          match.id,
                          Number(e.target.value),
                          liveScore?.team2 || 0
                        )
                      }
                      className="flex-1 px-2 py-1 border border-blue-300 rounded text-center text-sm"
                      placeholder="0"
                    />
                    <span className="text-blue-600 font-bold">-</span>
                    <input
                      type="number"
                      min="0"
                      max="13"
                      value={liveScore?.team2 || 0}
                      onChange={(e) =>
                        onLiveScoreUpdate(
                          match.id,
                          liveScore?.team1 || 0,
                          Number(e.target.value)
                        )
                      }
                      className="flex-1 px-2 py-1 border border-blue-300 rounded text-center text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>
              )}

              {/* Score Entry Button */}
              {!isCompleted && (
                <button
                  onClick={onEnterScore}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Enter Final Score
                </button>
              )}

              {/* Winner Display */}
              {isCompleted && match.result && (
                <div className="bg-success-100 border border-success-200 rounded-md p-2">
                  <div className="flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-success-600 mr-2" />
                    <span className="text-sm font-medium text-success-800">
                      Winner:{" "}
                      {match.result.winner === match.team1.id
                        ? match.team1.name
                        : match.team2.name}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourtDisplay;
